import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Dialog } from '@app/components/Shared/Dialog';
import { attributesByClinicalDepartmentSelector } from '@app/domain/catalog';
import { Attribute, AttributeGroupName } from '@app/types/catalog';
import { getAttributesGroupByName } from '@app/utils/catalog';

interface AttributesSelectionModalProps {
  onClickSubmit: (attributes: string[]) => void;
  onClose: () => void;
  open: boolean;
  selected: string[];
}

export function AttributesSelectionModal({
  onClickSubmit,
  onClose,
  open,
  selected,
}: AttributesSelectionModalProps): ReactElement {
  const attributesByClinicalDepartment = useRecoilValue(
    attributesByClinicalDepartmentSelector
  );
  const [checked, setChecked] = useState<string[]>([...selected]);

  useEffect(() => {
    setChecked(selected);
  }, [selected]);

  const clinicalDeptItems = useMemo(() => {
    return getAttributesGroupByName(
      attributesByClinicalDepartment,
      AttributeGroupName.CLINICAL_DEPARTMENT
    );
  }, [attributesByClinicalDepartment]);

  const handleChangeChecked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const targets: string[] = [];
      targets.push(event.target.value);
      setChecked(
        event.target.checked
          ? [...checked, ...targets]
          : checked.filter((c) => !targets.includes(c))
      );
    },
    [checked]
  );

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="診療科を選択"
      content={
        <>
          {clinicalDeptItems.map((item: Attribute) => (
            <Accordion key={item.id} TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ height: '48px' }}
              >
                <Typography>{item.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1}>
                  {item.children?.map((child) => (
                    <Grid item xs={6} key={child.id}>
                      <FormControlLabel
                        label={child.items[0].value}
                        control={
                          <Checkbox
                            value={child.items[0].value}
                            checked={checked.includes(child.items[0].value)}
                            onChange={(e) => handleChangeChecked(e)}
                          />
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      }
      action={
        <Stack direction="row" spacing={2} width={1}>
          <Button
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={handleClose}
          >
            キャンセル
          </Button>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            fullWidth
            onClick={() => onClickSubmit(checked)}
          >
            決定
          </Button>
        </Stack>
      }
    />
  );
}
